/**
 * Build a SecureMyName bulk-search deep-link URL from the 10 generated
 * Lites options.
 *
 * SMN bulk-search syntax (per the SMN agent's spec):
 *   https://app.securemyname.com/search?bulk=1&names=<csv-stems>
 *
 *   - `bulk=1` forces bulk mode regardless of the user's stored
 *     `smn.searchBulk` preference.
 *   - `names` is up to 10 stems (no .tld), comma- or %0A-separated.
 *     Backend truncates beyond 10.
 *   - TLD is sticky on the SMN side (`smn.searchBulkTld` in their
 *     localStorage). We deliberately do NOT send `tld=` — the user's
 *     SMN preference wins, per spec.
 *
 * Stems-only is enforced here: if the generated option contains a
 * dot (the LLM occasionally appends ".com"), take the first segment.
 * Non-domain prompts (where the option is a phrase or product name)
 * pass through unchanged.
 */
import type { DomainOption } from '../types';

const SMN_BULK_URL = 'https://app.securemyname.com/search';
const MAX_NAMES = 10;

export function buildSmnBulkUrl(options: DomainOption[]): string {
  const stems = options
    .slice(0, MAX_NAMES)
    .map((o) => {
      const raw = String(o?.domain ?? '').trim();
      // Strip any trailing .tld so SMN receives stems only. split('.')[0]
      // is the correct rule for the names Lites produces (single-SLD
      // candidates); multi-dot names (e.g. "co.uk") aren't generated.
      return raw.split('.')[0] ?? raw;
    })
    .filter((s) => s.length > 0);

  if (stems.length === 0) {
    return SMN_BULK_URL;
  }

  const params = new URLSearchParams();
  params.set('bulk', '1');
  params.set('names', stems.join(','));
  return `${SMN_BULK_URL}?${params.toString()}`;
}
